import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import {
  Map as MapIcon, Users, Building2, QrCode, Handshake, Sparkles,
  Check, ArrowRight, Flame
} from "lucide-react";

const nav = [
  { to: "/pricing", label: "Harga" },
  { to: "/login", label: "Masuk" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0D14] text-slate-100 overflow-hidden">
      <PublicNavbar />

      {/* Hero */}
      <section className="relative px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-orange-500/30 bg-orange-500/10 text-xs font-semibold text-orange-300 mb-6" data-testid="hero-badge">
              <Flame className="w-3.5 h-3.5" /> Platform Properti #1 untuk Agent Indonesia
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-balance">
              Katalog pribadi, CRM, co-broke &amp; peta canvassing —{" "}
              <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                semuanya satu platform.
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-400 max-w-2xl leading-relaxed">
              Dari canvassing di lapangan sampai closing di kantor. ListingPro bantu agent
              properti Indonesia manage listing, kelola lead, dan bagi komisi co-broke secara transparan.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta-register">
                <Button className="bg-gradient-to-r from-[#FF5722] to-[#F97316] hover:from-[#F97316] hover:to-[#EA580C] text-white font-semibold px-6 py-6 rounded-xl shadow-lg shadow-orange-500/20">
                  Mulai Gratis <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-cta-pricing">
                <Button variant="outline" className="border-slate-700 bg-transparent hover:bg-white/5 text-slate-200 px-6 py-6 rounded-xl">
                  Lihat Paket
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Gratis 30 hari semua fitur</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Watermark otomatis</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> QR code tiap listing</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-orange-500/20 to-blue-500/10 blur-3xl rounded-full" />
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1613553507747-5f8d62ad5904?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Luxury property"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute bottom-5 left-5 right-5 bg-black/50 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                <div className="text-[11px] uppercase tracking-widest text-orange-400 font-semibold mb-1">Featured Listing</div>
                <div className="text-lg font-bold">Villa Luxury Modern Seminyak</div>
                <div className="text-sm text-slate-300">Seminyak, Bali · Rp 7,8 M</div>
              </div>
              <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-md border border-white/10 text-xs font-semibold text-white">
                ListingPro · Budi Santoso
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-widest text-orange-400 font-semibold">3 Modul Inti</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-2 tracking-tight">
              Semua tools yang agent butuh — tanpa pindah-pindah aplikasi.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Users className="w-5 h-5" />}
              title="CRM Terpusat"
              desc="Lead inbox dengan auto-tag Hot/Warm/Cold. Follow-up reminder H+1, H+3, H+7 setelah viewing. Auto-match buyer dengan listing berdasarkan budget &amp; lokasi."
              testid="feature-crm"
            />
            <FeatureCard
              icon={<Building2 className="w-5 h-5" />}
              title="Manajemen Listing"
              desc="Upload foto multiple dengan watermark otomatis, Kanban board Baru → On Market → Nego → Closing → SOLD, dan QR code tiap listing untuk langsung ke WA."
              testid="feature-listing"
            />
            <FeatureCard
              icon={<Handshake className="w-5 h-5" />}
              title="Co-Broke & Radar"
              desc="Peta canvassing digital, kolaborasi antar agent dengan split komisi transparan, dan laporan mingguan ke owner properti."
              testid="feature-cobroke"
            />
          </div>
        </div>
      </section>

      {/* How */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 border-t border-white/5 bg-[#0C1120]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <span className="text-xs uppercase tracking-widest text-orange-400 font-semibold">Workflow Harian</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-2 tracking-tight">
              Dari canvassing sampai closing — satu alur yang rapi.
            </h2>
            <div className="mt-8 space-y-5">
              {[
                { n: "01", t: "Canvassing di peta", d: "Tandai rumah di peta — potensi jual, sudah dikontak, atau catatan khusus." },
                { n: "02", t: "Input listing cepat", d: "Multi-foto upload. Watermark otomatis pakai nama/logo agent." },
                { n: "03", t: "Share lewat QR / WA", d: "Scan QR = buka detail + tombol WhatsApp langsung ke agent." },
                { n: "04", t: "Kelola lead", d: "Auto-tag Hot/Warm/Cold dan follow-up reminder otomatis setelah viewing." },
                { n: "05", t: "Co-broke &amp; laporan", d: "Split komisi transparan. Laporan mingguan otomatis ke owner." },
              ].map((s) => (
                <div key={s.n} className="flex gap-5">
                  <div className="text-orange-500 font-mono font-bold text-sm pt-1">{s.n}</div>
                  <div>
                    <div className="font-semibold text-slate-100">{s.t}</div>
                    <div className="text-slate-400 text-sm mt-0.5">{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            {[
              "https://images.unsplash.com/photo-1538429514009-cd5e56785ffb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
              "https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
              "https://images.unsplash.com/photo-1675657144518-025804f1812c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
              "https://images.unsplash.com/photo-1613553507747-5f8d62ad5904?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
            ].map((u, i) => (
              <div key={i} className={`rounded-xl overflow-hidden border border-white/10 ${i % 2 ? "translate-y-6" : ""}`}>
                <img src={u} alt="" className="w-full h-56 object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-24">
        <div className="max-w-5xl mx-auto rounded-3xl border border-orange-500/20 bg-gradient-to-br from-[#141C2E] to-[#0D1322] p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-orange-500/20 blur-3xl rounded-full" />
          <div className="relative">
            <Sparkles className="w-7 h-7 text-orange-400 mx-auto mb-4" />
            <h3 className="text-3xl sm:text-4xl font-bold tracking-tight">Siap jadi agent paling efisien di kota Anda?</h3>
            <p className="mt-3 text-slate-400 max-w-2xl mx-auto">
              Coba gratis 30 hari dengan semua fitur — tanpa kartu kredit.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/register" data-testid="cta-register-bottom">
                <Button className="bg-orange-500 hover:bg-orange-600 font-semibold px-6 py-6 rounded-xl">
                  Buat Akun Gratis
                </Button>
              </Link>
              <Link to="/pricing" data-testid="cta-pricing-bottom">
                <Button variant="outline" className="border-slate-700 bg-transparent hover:bg-white/5 text-slate-200 px-6 py-6 rounded-xl">
                  Lihat Paket Pro
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

function FeatureCard({ icon, title, desc, testid }) {
  return (
    <div data-testid={testid} className="relative group rounded-2xl p-6 border border-white/5 bg-[#141C2E] hover:border-orange-500/40 transition-all duration-300">
      <div className="w-10 h-10 rounded-lg bg-orange-500/10 border border-orange-500/20 grid place-items-center text-orange-400 mb-5">
        {icon}
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</p>
    </div>
  );
}

export function PublicNavbar() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0D14]/85 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="public-logo">
          <Brand />
        </Link>
        <nav className="flex items-center gap-6">
          {nav.map((n) => (
            <Link key={n.to} to={n.to} className="text-sm text-slate-300 hover:text-orange-400 transition" data-testid={`public-nav-${n.label.toLowerCase()}`}>
              {n.label}
            </Link>
          ))}
          <Link to="/register" data-testid="public-cta-register">
            <Button className="bg-orange-500 hover:bg-orange-600 font-semibold">Daftar</Button>
          </Link>
        </nav>
      </div>
    </header>
  );
}
